import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { register } from '../services/api';

export default function RegisterPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setLoading(true);
    try {
      await register({
        email,
        password,
        first_name: firstName,
        last_name: lastName,
        company_name: companyName,
      });
      navigate('/onboarding');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    background: 'var(--bg-input)',
    border: '1px solid var(--border-primary)',
    color: 'var(--text-primary)',
  };

  return (
    <div
      className="page-enter min-h-screen flex items-center justify-center"
      style={{ background: 'var(--bg-primary)' }}
    >
      <div className="w-full max-w-[400px] px-6">
        {/* Logo */}
        <div className="text-center mb-10">
          <h1
            className="font-display text-5xl mb-2"
            style={{ color: 'var(--netrun-green)' }}
          >
            KOG
          </h1>
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
            Create your account
          </p>
        </div>

        {/* Register form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="flex gap-3">
            <div className="flex-1">
              <label
                className="block text-xs mb-1.5"
                style={{ color: 'var(--text-secondary)' }}
              >
                First Name
              </label>
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
                className="w-full px-4 py-3 rounded-lg text-sm outline-none transition-all"
                style={inputStyle}
                onFocus={(e) =>
                  (e.currentTarget.style.borderColor = 'var(--netrun-green)')
                }
                onBlur={(e) =>
                  (e.currentTarget.style.borderColor = 'var(--border-primary)')
                }
              />
            </div>
            <div className="flex-1">
              <label
                className="block text-xs mb-1.5"
                style={{ color: 'var(--text-secondary)' }}
              >
                Last Name
              </label>
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="w-full px-4 py-3 rounded-lg text-sm outline-none transition-all"
                style={inputStyle}
                onFocus={(e) =>
                  (e.currentTarget.style.borderColor = 'var(--netrun-green)')
                }
                onBlur={(e) =>
                  (e.currentTarget.style.borderColor = 'var(--border-primary)')
                }
              />
            </div>
          </div>

          <div>
            <label
              className="block text-xs mb-1.5"
              style={{ color: 'var(--text-secondary)' }}
            >
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full px-4 py-3 rounded-lg text-sm outline-none transition-all"
              style={inputStyle}
              onFocus={(e) =>
                (e.currentTarget.style.borderColor = 'var(--netrun-green)')
              }
              onBlur={(e) =>
                (e.currentTarget.style.borderColor = 'var(--border-primary)')
              }
            />
          </div>

          <div>
            <label
              className="block text-xs mb-1.5"
              style={{ color: 'var(--text-secondary)' }}
            >
              Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
              className="w-full px-4 py-3 rounded-lg text-sm outline-none transition-all"
              style={inputStyle}
              onFocus={(e) =>
                (e.currentTarget.style.borderColor = 'var(--netrun-green)')
              }
              onBlur={(e) =>
                (e.currentTarget.style.borderColor = 'var(--border-primary)')
              }
            />
            <p
              className="text-xs mt-1"
              style={{ color: 'var(--text-muted)' }}
            >
              Minimum 8 characters
            </p>
          </div>

          <div>
            <label
              className="block text-xs mb-1.5"
              style={{ color: 'var(--text-secondary)' }}
            >
              Company Name{' '}
              <span style={{ color: 'var(--text-muted)' }}>(optional)</span>
            </label>
            <input
              type="text"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              className="w-full px-4 py-3 rounded-lg text-sm outline-none transition-all"
              style={inputStyle}
              onFocus={(e) =>
                (e.currentTarget.style.borderColor = 'var(--netrun-green)')
              }
              onBlur={(e) =>
                (e.currentTarget.style.borderColor = 'var(--border-primary)')
              }
            />
          </div>

          {error && (
            <p
              className="text-xs px-3 py-2 rounded-lg"
              style={{
                background: 'rgba(239,68,68,0.1)',
                color: 'var(--error)',
                border: '1px solid rgba(239,68,68,0.3)',
              }}
            >
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 rounded-lg text-sm font-display cursor-pointer transition-all duration-200"
            style={{
              background: loading
                ? 'var(--border-subtle)'
                : 'var(--netrun-green)',
              color: 'var(--netrun-black)',
              border: 'none',
            }}
          >
            {loading ? 'Creating account...' : 'Create Account'}
          </button>
        </form>

        <p
          className="text-center text-xs mt-6"
          style={{ color: 'var(--text-muted)' }}
        >
          Already have an account?{' '}
          <Link
            to="/login"
            className="underline"
            style={{ color: 'var(--netrun-green)' }}
          >
            Sign in
          </Link>
        </p>

        <p
          className="text-center text-xs mt-8"
          style={{ color: 'var(--text-muted)' }}
        >
          Powered by Netrun Systems
        </p>
      </div>
    </div>
  );
}
